import { useState, useCallback, useEffect } from 'react';
import { useNotification } from '@/stores/notificationStore';
import { HttpAdminAdapter } from '@/services/admin/HttpAdminAdapter';
import type {
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  User,
} from '@/services/admin/AdminService';

/**
 * Hook for managing organizations with full CRUD operations
 */
export const useOrganizations = () => {
  const [adminService] = useState(() => new HttpAdminAdapter());
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const { showNotification } = useNotification();

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.listOrganizations();
      setOrganizations(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch organizations';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminService]);

  const createOrganization = useCallback(
    async (data: CreateOrganizationRequest): Promise<Organization | null> => {
      setActionLoading(true);
      try {
        const newOrg = await adminService.createOrganization(data);
        setOrganizations((prev) => [...prev, newOrg]);
        showNotification(
          `Organization "${data.name}" created successfully`,
          'success'
        );
        return newOrg;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create organization';
        showNotification(errorMessage, 'error');
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [adminService, showNotification]
  );

  const updateOrganization = useCallback(
    async (
      orgId: string,
      data: UpdateOrganizationRequest
    ): Promise<Organization | null> => {
      setActionLoading(true);
      try {
        const updated = await adminService.updateOrganization(orgId, data);
        setOrganizations((prev) =>
          prev.map((org) => (org.id === orgId ? updated : org))
        );
        showNotification('Organization updated successfully', 'success');
        return updated;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to update organization';
        showNotification(errorMessage, 'error');
        return null;
      } finally {
        setActionLoading(false);
      }
    },
    [adminService, showNotification]
  );

  const deleteOrganization = useCallback(
    async (orgId: string): Promise<boolean> => {
      setActionLoading(true);
      try {
        await adminService.deleteOrganization(orgId);
        setOrganizations((prev) => prev.filter((org) => org.id !== orgId));
        showNotification('Organization deactivated successfully', 'success');
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to deactivate organization';
        showNotification(errorMessage, 'error');
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [adminService, showNotification]
  );

  const getOrganizationUsers = useCallback(
    async (orgId: string): Promise<User[]> => {
      try {
        return await adminService.getOrganizationUsers(orgId);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to fetch organization users';
        showNotification(errorMessage, 'error');
        return [];
      }
    },
    [adminService, showNotification]
  );

  const addUserToOrganization = useCallback(
    async (orgId: string, uid: string): Promise<boolean> => {
      setActionLoading(true);
      try {
        await adminService.addUserToOrganization(orgId, uid);
        showNotification('User added to organization', 'success');
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to add user to organization';
        showNotification(errorMessage, 'error');
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [adminService, showNotification]
  );

  const removeUserFromOrganization = useCallback(
    async (orgId: string, uid: string): Promise<boolean> => {
      setActionLoading(true);
      try {
        await adminService.removeUserFromOrganization(orgId, uid);
        showNotification('User removed from organization', 'success');
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to remove user from organization';
        showNotification(errorMessage, 'error');
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [adminService, showNotification]
  );

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  return {
    organizations,
    loading,
    error,
    actionLoading,
    fetchOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    getOrganizationUsers,
    addUserToOrganization,
    removeUserFromOrganization,
  };
};

export default useOrganizations;
